import React from "react";
import {
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  ScrollText,
  FileBadge2,
  Building2,
  Camera,
  ListChecks,
} from "lucide-react";
import { ImageUploader } from "@/admin/components/ImageUploader";

/* ------------------------------------------------------------------
   Visual form editor for the "Sejarah" site-content document.
   Shape: { narasi, timeline[], perizinan[], klasifikasi{}, photos[] }
   Parent owns the object; this component emits the full updated object
   via onChange on every edit.
   ------------------------------------------------------------------ */

const PERIZINAN_ICONS = ["FileBadge2", "ShieldCheck", "Award", "Building2"];

const uid = (p = "item") => `${p}-${Math.random().toString(36).slice(2, 8)}`;

const inputCls =
  "w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 bg-white";

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-slate-400 mt-1">{hint}</span>}
  </label>
);

const Text = ({ value, onChange, placeholder, testid }) => (
  <input
    type="text"
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    data-testid={testid}
    className={inputCls}
  />
);

const Area = ({ value, onChange, rows = 4, placeholder, testid }) => (
  <textarea
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    rows={rows}
    placeholder={placeholder}
    data-testid={testid}
    className={`${inputCls} leading-relaxed`}
  />
);

// immutable helpers for arrays
const arrayOps = (arr, onChange) => {
  const list = Array.isArray(arr) ? arr : [];
  return {
    list,
    set: (i, v) => onChange(list.map((x, idx) => (idx === i ? v : x))),
    add: (v) => onChange([...list, v]),
    remove: (i) => onChange(list.filter((_, idx) => idx !== i)),
    move: (i, dir) => {
      const j = i + dir;
      if (j < 0 || j >= list.length) return;
      const copy = list.slice();
      const [it] = copy.splice(i, 1);
      copy.splice(j, 0, it);
      onChange(copy);
    },
  };
};

const ItemShell = ({ title, index, count, onMove, onRemove, children, testid }) => (
  <div
    data-testid={testid}
    className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 space-y-3"
  >
    <div className="flex items-center justify-between">
      <div className="text-xs font-semibold text-slate-500">{title}</div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onMove(-1)}
          disabled={index === 0}
          title="Naikkan"
          className="h-7 w-7 rounded-lg text-slate-500 hover:bg-slate-200 disabled:opacity-30 flex items-center justify-center"
        >
          <ArrowUp className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => onMove(1)}
          disabled={index === count - 1}
          title="Turunkan"
          className="h-7 w-7 rounded-lg text-slate-500 hover:bg-slate-200 disabled:opacity-30 flex items-center justify-center"
        >
          <ArrowDown className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          title="Hapus"
          className="h-7 w-7 rounded-lg text-red-500 hover:bg-red-50 flex items-center justify-center"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
    {children}
  </div>
);

const SectionCard = ({ icon: Icon, title, desc, children, action }) => (
  <section className="rounded-3xl border border-slate-100 bg-white shadow-sm p-5 md:p-6 space-y-4">
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl bg-blue-50 text-blue-800 flex items-center justify-center">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="font-heading font-semibold text-slate-900">{title}</div>
          {desc && <div className="text-xs text-slate-500">{desc}</div>}
        </div>
      </div>
      {action}
    </div>
    {children}
  </section>
);

const AddButton = ({ onClick, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-800 hover:bg-blue-900 text-white"
  >
    <Plus className="h-3.5 w-3.5" />
    {label}
  </button>
);

const SejarahForm = ({ value, onChange }) => {
  const doc = value || {};
  const patch = (p) => onChange({ ...doc, ...p });

  const timeline = arrayOps(doc.timeline, (v) => patch({ timeline: v }));
  const perizinan = arrayOps(doc.perizinan, (v) => patch({ perizinan: v }));
  const photos = arrayOps(doc.photos, (v) => patch({ photos: v }));
  const klas = doc.klasifikasi || {};
  const patchKlas = (p) => patch({ klasifikasi: { ...klas, ...p } });
  const layanan = arrayOps(klas.layanan, (v) => patchKlas({ layanan: v }));

  return (
    <div className="space-y-5" data-testid="sejarah-form">
      {/* Narasi */}
      <SectionCard icon={ScrollText} title="Narasi Sejarah" desc="Paragraf pembuka halaman Sejarah">
        <Field label="Narasi">
          <Area
            value={doc.narasi}
            onChange={(v) => patch({ narasi: v })}
            rows={6}
            placeholder="Tulis narasi sejarah rumah sakit…"
            testid="sejarah-narasi"
          />
        </Field>
      </SectionCard>

      {/* Timeline */}
      <SectionCard
        icon={ScrollText}
        title="Linimasa (Timeline)"
        desc="Peristiwa penting berurutan tahun"
        action={
          <AddButton
            testid="sejarah-timeline-add"
            label="Tambah Peristiwa"
            onClick={() => timeline.add({ year: "", title: "", body: "" })}
          />
        }
      >
        {timeline.list.length === 0 && (
          <div className="text-xs text-slate-400">Belum ada peristiwa. Klik tombol Tambah Peristiwa.</div>
        )}
        <div className="space-y-3">
          {timeline.list.map((ev, i) => (
            <ItemShell
              key={i}
              testid={`sejarah-timeline-item-${i}`}
              title={`Peristiwa #${i + 1}`}
              index={i}
              count={timeline.list.length}
              onMove={(d) => timeline.move(i, d)}
              onRemove={() => timeline.remove(i)}
            >
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Field label="Tahun">
                  <Text value={ev.year} onChange={(v) => timeline.set(i, { ...ev, year: v })} placeholder="2015" />
                </Field>
                <div className="md:col-span-3">
                  <Field label="Judul">
                    <Text value={ev.title} onChange={(v) => timeline.set(i, { ...ev, title: v })} placeholder="Judul peristiwa" />
                  </Field>
                </div>
              </div>
              <Field label="Keterangan">
                <Area value={ev.body} onChange={(v) => timeline.set(i, { ...ev, body: v })} rows={2} />
              </Field>
            </ItemShell>
          ))}
        </div>
      </SectionCard>

      {/* Perizinan */}
      <SectionCard
        icon={FileBadge2}
        title="Perizinan"
        desc="Kartu izin/akreditasi rumah sakit"
        action={
          <AddButton
            testid="sejarah-perizinan-add"
            label="Tambah Izin"
            onClick={() => perizinan.add({ icon: "FileBadge2", title: "", detail: "", valid: "" })}
          />
        }
      >
        {perizinan.list.length === 0 && (
          <div className="text-xs text-slate-400">Belum ada perizinan.</div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {perizinan.list.map((p, i) => (
            <ItemShell
              key={i}
              testid={`sejarah-perizinan-item-${i}`}
              title={`Izin #${i + 1}`}
              index={i}
              count={perizinan.list.length}
              onMove={(d) => perizinan.move(i, d)}
              onRemove={() => perizinan.remove(i)}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Ikon">
                  <select
                    value={p.icon || "FileBadge2"}
                    onChange={(e) => perizinan.set(i, { ...p, icon: e.target.value })}
                    className={inputCls}
                  >
                    {PERIZINAN_ICONS.map((ic) => (
                      <option key={ic} value={ic}>{ic}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Judul">
                  <Text value={p.title} onChange={(v) => perizinan.set(i, { ...p, title: v })} placeholder="Izin Operasional" />
                </Field>
              </div>
              <Field label="Detail">
                <Text value={p.detail} onChange={(v) => perizinan.set(i, { ...p, detail: v })} placeholder="SK Kemenkes RI No. …" />
              </Field>
              <Field label="Masa Berlaku / Keterangan">
                <Text value={p.valid} onChange={(v) => perizinan.set(i, { ...p, valid: v })} placeholder="Berlaku 2024 – 2027" />
              </Field>
            </ItemShell>
          ))}
        </div>
      </SectionCard>

      {/* Klasifikasi */}
      <SectionCard icon={Building2} title="Tipe & Klasifikasi" desc="Data kelas & layanan rumah sakit">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Tipe / Kelas">
            <Text value={klas.tipe} onChange={(v) => patchKlas({ tipe: v })} placeholder="Rumah Sakit Umum Kelas C" testid="sejarah-klas-tipe" />
          </Field>
          <Field label="Jenis">
            <Text value={klas.jenis} onChange={(v) => patchKlas({ jenis: v })} placeholder="Rumah Sakit Umum" />
          </Field>
          <Field label="Kepemilikan">
            <Text value={klas.kepemilikan} onChange={(v) => patchKlas({ kepemilikan: v })} placeholder="Polri" />
          </Field>
          <Field label="Kapasitas">
            <Text value={klas.kapasitas} onChange={(v) => patchKlas({ kapasitas: v })} placeholder="60+ Tempat Tidur" />
          </Field>
          <Field label="IGD">
            <Text value={klas.igd} onChange={(v) => patchKlas({ igd: v })} placeholder="24 Jam / 7 Hari" />
          </Field>
        </div>

        <div className="pt-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <ListChecks className="h-4 w-4 text-blue-800" />
              Daftar Layanan
            </div>
            <AddButton
              testid="sejarah-layanan-add"
              label="Tambah Layanan"
              onClick={() => layanan.add("")}
            />
          </div>
          <div className="space-y-2">
            {layanan.list.map((l, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`sejarah-layanan-item-${i}`}>
                <span className="h-6 w-6 shrink-0 rounded-full bg-blue-800 text-white text-[10px] font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <Text value={l} onChange={(v) => layanan.set(i, v)} placeholder="Nama layanan" />
                <button
                  type="button"
                  onClick={() => layanan.remove(i)}
                  className="h-8 w-8 shrink-0 rounded-lg text-red-500 hover:bg-red-50 flex items-center justify-center"
                  title="Hapus"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            {layanan.list.length === 0 && (
              <div className="text-xs text-slate-400">Belum ada layanan.</div>
            )}
          </div>
        </div>
      </SectionCard>

      {/* Foto peresmian */}
      <SectionCard
        icon={Camera}
        title="Foto Peresmian"
        desc="Galeri foto di bagian bawah halaman"
        action={
          <AddButton
            testid="sejarah-photo-add"
            label="Tambah Foto"
            onClick={() => photos.add({ id: uid("photo"), src: "", caption: "", subtitle: "" })}
          />
        }
      >
        {photos.list.length === 0 && (
          <div className="text-xs text-slate-400">Belum ada foto.</div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {photos.list.map((p, i) => (
            <ItemShell
              key={p.id || i}
              testid={`sejarah-photo-item-${i}`}
              title={`Foto #${i + 1}`}
              index={i}
              count={photos.list.length}
              onMove={(d) => photos.move(i, d)}
              onRemove={() => photos.remove(i)}
            >
              <Field label="Gambar">
                <ImageUploader
                  value={p.src}
                  onChange={(url) => photos.set(i, { ...p, id: p.id || uid("photo"), src: url })}
                  testid={`sejarah-photo-uploader-${i}`}
                  defaultAspect="4:3"
                />
              </Field>
              <Field label="Caption">
                <Text value={p.caption} onChange={(v) => photos.set(i, { ...p, caption: v })} placeholder="Judul foto" />
              </Field>
              <Field label="Sub-caption">
                <Text value={p.subtitle} onChange={(v) => photos.set(i, { ...p, subtitle: v })} placeholder="Keterangan tambahan" />
              </Field>
            </ItemShell>
          ))}
        </div>
      </SectionCard>
    </div>
  );
};

export default SejarahForm;
